import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  CheckCircle2,
  Plus,
  Building2,
  X,
  Search,
  Filter,
  Edit2,
  Trash2,
  Eye,
  ShieldCheck,
  Phone,
  Mail,
  MapPin,
  PawPrint,
  FileCheck2,
} from "lucide-react";
import { toast } from "sonner";
import { PageHead, Panel, StatCard } from "@/project/components/dashboard-shell";
import { institutions, type Institution } from "@/project/data/sample";

export const Route = createFileRoute("/admin/institutions")({
  component: AdminInstitutions,
});

export function AdminInstitutions() {
  const [instList, setInstList] = useState<Institution[]>(institutions);
  const [openModal, setOpenModal] = useState(false);
  const [editingInst, setEditingInst] = useState<Institution | null>(null);
  const [viewingInst, setViewingInst] = useState<Institution | null>(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string>("All");
  const [statusFilter, setStatusFilter] = useState<string>("All");

  // Form State
  const [name, setName] = useState("");
  const [type, setType] = useState<Institution["type"]>("Gaushala");
  const [city, setCity] = useState("");
  const [state, setState] = useState("Gujarat");
  const [animalCount, setAnimalCount] = useState<number>(500);
  const [shelterCapacity, setShelterCapacity] = useState<number>(800);
  const [has80G, setHas80G] = useState(true);
  const [phone, setPhone] = useState("");
  const [about, setAbout] = useState("");

  const filteredList = instList.filter((inst) => {
    const matchesSearch =
      inst.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inst.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === "All" || inst.type === selectedType;
    const matchesStatus =
      statusFilter === "All"
        ? true
        : statusFilter === "Verified"
        ? inst.verified
        : !inst.verified;
    return matchesSearch && matchesType && matchesStatus;
  });

  function openCreateModal() {
    setEditingInst(null);
    setName("");
    setCity("");
    setPhone("");
    setAbout("");
    setType("Gaushala");
    setAnimalCount(500);
    setShelterCapacity(800);
    setHas80G(true);
    setOpenModal(true);
  }

  function openEditModal(inst: Institution) {
    setEditingInst(inst);
    setName(inst.name);
    setCity(inst.city);
    setState(inst.state || "Gujarat");
    setType(inst.type);
    setAnimalCount(inst.animalCount);
    setShelterCapacity(inst.shelterCapacity);
    setHas80G(inst.has80G);
    setPhone(inst.phone || "");
    setAbout(inst.about || "");
    setOpenModal(true);
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !city.trim()) {
      toast.error("Please fill in institution name and city.");
      return;
    }

    if (editingInst) {
      // Edit existing
      setInstList((prev) =>
        prev.map((item) =>
          item.id === editingInst.id
            ? {
                ...item,
                name,
                type,
                city,
                state,
                animalCount,
                shelterCapacity,
                has80G,
                phone: phone || item.phone,
                about: about || item.about,
              }
            : item
        )
      );
      toast.success(`Updated ${name} successfully!`);
    } else {
      // Create new
      const newInst: Institution = {
        id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        name,
        type,
        verified: true,
        city,
        state,
        address: `${city}, ${state}`,
        pincode: "380001",
        animalCount,
        shelterCapacity,
        established: 2026,
        animals: ["Cows", "Calves", "Bulls"],
        has80G,
        about:
          about ||
          `Verified ${type} shelter operating in ${city}, ${state} providing 24x7 fodder and care.`,
        mission: `Providing lifelong sanctuary and medical care for animals in ${city}.`,
        cover:
          "https://images.unsplash.com/photo-1546445317-29f4545f9d52?auto=format&fit=crop&w=800&q=80",
        activeCauses: 1,
        totalDonations: 50000,
        contactPerson: "Manager",
        phone: phone || "+91 98765 43210",
        email: `contact@${name.toLowerCase().replace(/[^a-z0-9]+/g, "")}.org`,
        registrationNo: "GUJ/AHM/1042/2026",
      };
      setInstList([newInst, ...instList]);
      toast.success(`Successfully added & verified ${name}!`);
    }

    setOpenModal(false);
  }

  function toggleVerification(id: string) {
    setInstList((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const next = !item.verified;
          toast.success(
            next
              ? `Approved ${item.name}`
              : `Suspended verification for ${item.name}`
          );
          return { ...item, verified: next };
        }
        return item;
      })
    );
  }

  function handleDelete(inst: Institution) {
    if (confirm(`Are you sure you want to remove ${inst.name}?`)) {
      setInstList((prev) => prev.filter((i) => i.id !== inst.id));
      toast.success(`Removed ${inst.name}`);
    }
  }

  return (
    <>
      <PageHead
        title="Institutions & Gaushalas"
        subtitle="Manage, verify, edit, and onboard animal welfare institutions across Gujarat & India."
        action={
          <button
            onClick={openCreateModal}
            className="flex items-center gap-1.5 rounded-full bg-[var(--maroon)] px-5 py-2.5 text-xs font-bold text-white shadow-md hover:bg-[var(--maroon-deep)] transition active:scale-95"
          >
            <Plus className="h-4 w-4" /> Add Institution
          </button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard
          label="Total Listed"
          value={String(instList.length)}
          tone="primary"
        />
        <StatCard
          label="Admin Verified"
          value={String(instList.filter((i) => i.verified).length)}
          tone="good"
        />
        <StatCard
          label="80G Certified"
          value={String(instList.filter((i) => i.has80G).length)}
          tone="warn"
        />
        <StatCard
          label="Total Shelter Capacity"
          value={instList
            .reduce((s, i) => s + i.shelterCapacity, 0)
            .toLocaleString("en-IN")}
        />
      </div>

      <Panel
        title="All Listed Institutions"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full border border-border/80 bg-white px-3 py-1 text-xs">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent outline-none w-36 sm:w-48 placeholder:text-muted-foreground"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="rounded-full border border-border/80 bg-white px-3 py-1 text-xs font-semibold outline-none cursor-pointer"
            >
              <option value="All">All Types</option>
              <option value="Gaushala">Gaushalas</option>
              <option value="Panjrapol">Panjrapoles</option>
              <option value="Bird Hospital">Bird Hospitals</option>
              <option value="Stray Shelter">Stray Shelters</option>
            </select>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/30">
              <tr className="text-left">
                <th className="py-3 px-3">Institution Name</th>
                <th className="px-3">Type</th>
                <th className="px-3">City / State</th>
                <th className="px-3">Animals</th>
                <th className="px-3">80G</th>
                <th className="px-3">Verification</th>
                <th className="text-right px-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filteredList.map((i) => (
                <tr
                  key={i.id}
                  className="hover:bg-[var(--cream-2)]/60 transition-colors"
                >
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={i.cover}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-xl object-cover border border-border/60"
                      />
                      <div>
                        <p className="font-bold text-[var(--maroon-deep)] leading-tight">
                          {i.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Reg: {i.registrationNo || "GUJ/AHM/1042"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 font-medium text-foreground/80">
                    {i.type}
                  </td>
                  <td className="px-3 text-muted-foreground">
                    {i.city}, {i.state}
                  </td>
                  <td className="px-3 font-bold text-[var(--maroon-deep)]">
                    {i.animalCount.toLocaleString("en-IN")}
                  </td>
                  <td className="px-3">
                    {i.has80G ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200">
                        <FileCheck2 className="h-3 w-3" /> 80G Certified
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3">
                    <button
                      onClick={() => toggleVerification(i.id)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold transition cursor-pointer ${
                        i.verified
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                      }`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {i.verified ? "Approved" : "Pending"}
                    </button>
                  </td>
                  <td className="text-right px-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => setViewingInst(i)}
                        title="View Details"
                        className="grid h-8 w-8 place-items-center rounded-lg border border-border/60 bg-white hover:bg-muted text-foreground/75 transition"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => openEditModal(i)}
                        title="Edit Institution"
                        className="grid h-8 w-8 place-items-center rounded-lg border border-border/60 bg-white hover:bg-muted text-[var(--maroon)] transition"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(i)}
                        title="Delete"
                        className="grid h-8 w-8 place-items-center rounded-lg border border-border/60 bg-white hover:bg-red-50 text-red-600 transition"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* ── View Detail Drawer / Modal ───────────────────────────────── */}
      {viewingInst && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in"
          onClick={() => setViewingInst(null)}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl border border-border space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-44 overflow-hidden">
              <img
                src={viewingInst.cover}
                alt=""
                className="h-full w-full object-cover"
              />
              <button
                onClick={() => setViewingInst(null)}
                className="absolute top-3 right-3 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white hover:bg-black"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-3 left-4 text-white">
                <span className="rounded-full bg-[var(--saffron)] px-2.5 py-0.5 text-[10px] font-extrabold text-[var(--maroon-deep)] uppercase tracking-wider">
                  {viewingInst.type}
                </span>
                <h2 className="font-display text-xl font-bold mt-1 text-white">
                  {viewingInst.name}
                </h2>
              </div>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center gap-2 text-foreground/80">
                  <MapPin className="h-4 w-4 text-[var(--maroon)]" />
                  <span>
                    {viewingInst.city}, {viewingInst.state}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-foreground/80">
                  <PawPrint className="h-4 w-4 text-[var(--saffron-deep)]" />
                  <span>
                    {viewingInst.animalCount} Animals sheltered
                  </span>
                </div>
                <div className="flex items-center gap-2 text-foreground/80">
                  <Phone className="h-4 w-4 text-emerald-600" />
                  <span>{viewingInst.phone || "+91 98765 43210"}</span>
                </div>
                <div className="flex items-center gap-2 text-foreground/80">
                  <ShieldCheck className="h-4 w-4 text-indigo-600" />
                  <span>{viewingInst.has80G ? "80G Certified" : "Non-80G"}</span>
                </div>
              </div>

              <div className="border-t border-border pt-3">
                <p className="text-xs font-bold text-[var(--maroon-deep)] mb-1">
                  About & Mission
                </p>
                <p className="text-xs text-foreground/75 leading-relaxed">
                  {viewingInst.about}
                </p>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setViewingInst(null)}
                  className="rounded-full bg-[var(--maroon)] px-5 py-2 text-xs font-bold text-white hover:bg-[var(--maroon-deep)]"
                >
                  Close Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Institution Modal ─────────────────────────────── */}
      {openModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in">
          <div className="w-full max-w-lg rounded-3xl bg-white border border-border p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[var(--maroon)]" />
                <h3 className="font-display text-base font-bold text-[var(--maroon-deep)]">
                  {editingInst ? "Edit Institution" : "Add New Institution"}
                </h3>
              </div>
              <button
                onClick={() => setOpenModal(false)}
                className="grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-muted text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-foreground mb-1 block">
                  Institution Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Shree Krishna Gaushala Trust"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-base"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-foreground mb-1 block">
                    Institution Type
                  </label>
                  <select
                    value={type}
                    onChange={(e) =>
                      setType(e.target.value as Institution["type"])
                    }
                    className="input-base cursor-pointer"
                  >
                    <option value="Gaushala">Gaushala</option>
                    <option value="Panjrapol">Panjrapol</option>
                    <option value="Bird Hospital">Bird Hospital</option>
                    <option value="Stray Shelter">Stray Shelter</option>
                    <option value="Wildlife Rehabilitation">
                      Wildlife Rehab
                    </option>
                  </select>
                </div>
                <div>
                  <label className="font-bold text-foreground mb-1 block">
                    City *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Ahmedabad"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="input-base"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-bold text-foreground mb-1 block">
                    Animals
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={animalCount}
                    onChange={(e) => setAnimalCount(Number(e.target.value))}
                    className="input-base font-bold"
                  />
                </div>
                <div>
                  <label className="font-bold text-foreground mb-1 block">
                    Capacity
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={shelterCapacity}
                    onChange={(e) => setShelterCapacity(Number(e.target.value))}
                    className="input-base font-bold"
                  />
                </div>
                <div>
                  <label className="font-bold text-foreground mb-1 block">
                    80G Status
                  </label>
                  <select
                    value={has80G ? "yes" : "no"}
                    onChange={(e) => setHas80G(e.target.value === "yes")}
                    className="input-base font-bold cursor-pointer"
                  >
                    <option value="yes">80G Certified</option>
                    <option value="no">Non-80G</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-bold text-foreground mb-1 block">
                  Helpline / Phone
                </label>
                <input
                  type="text"
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input-base"
                />
              </div>

              <div>
                <label className="font-bold text-foreground mb-1 block">
                  About / Mission
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe shelter activities and animal care..."
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  className="input-base resize-none"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setOpenModal(false)}
                  className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-full bg-[var(--maroon)] px-5 py-2 text-xs font-bold text-white hover:bg-[var(--maroon-deep)] shadow-xs"
                >
                  {editingInst ? "Save Changes" : "Add & Verify Institution"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}